/** Firestore-like reference + snapshot classes. */

import type { DocumentData, Protocol } from './protocol';
import type { QueryConstraint } from './constraints';
import type { RethinkClient } from './client';

export interface FirestoreDataConverter<T> {
  toFirestore(modelObject: T): DocumentData;
  fromFirestore(snapshot: QueryDocumentSnapshot<DocumentData>): T;
}

export interface DocumentSnapshot<T = DocumentData> {
  id: string;
  exists: boolean;
  ref: DocumentReference<T>;
  data(): T | undefined;
}

export interface QueryDocumentSnapshot<T = DocumentData> extends DocumentSnapshot<T> {
  data(): T;
}

export interface DocumentChange<T = DocumentData> {
  type: 'added' | 'modified' | 'removed';
  doc: QueryDocumentSnapshot<T>;
  oldIndex: number; // -1 if added
  newIndex: number; // -1 if removed
}

export interface QuerySnapshot<T = DocumentData> {
  docs: QueryDocumentSnapshot<T>[];
  docChanges(): DocumentChange<T>[];
  empty: boolean; // --- FIXED: Declared natively on interface ---
  forEach(callback: (result: QueryDocumentSnapshot<T>) => void, thisArg?: any): void;
}

/**
 * Reference Classes
 */
export class CollectionReference<T = DocumentData> {
  // --- FIXED: Added native property mapping to satisfy TypeScript ---
  readonly path: string;

  constructor(
    public client: RethinkClient,
    public name: string,
    public converter: FirestoreDataConverter<T> | null = null,
  ) {
    this.path = name;
  }

  withConverter<U>(converter: FirestoreDataConverter<U>): CollectionReference<U> {
    return new CollectionReference<U>(this.client, this.name, converter);
  }
}

export class DocumentReference<T = DocumentData> {
  // --- FIXED: Added native property mapping to satisfy TypeScript ---
  readonly path: string;
  declare readonly __refType: T;

  constructor(
    public client: RethinkClient,
    public collectionName: string,
    public id: string,
  ) {
    this.path = collectionName ? `${collectionName}/${id}` : id;
  }
}

export class Query<T = DocumentData> {
  constructor(
    public colRef: CollectionReference<T>,
    public constraints: QueryConstraint[] = [],
    public isGroup: boolean = false,
  ) {}

  buildOptions(): Protocol.QueryOptions {
    const options: Protocol.QueryOptions = { filters: [], fields: [], orderBy: [] };
    this.constraints.forEach((c) => c.apply(options));
    return options;
  }
}

/**
 * Snapshot Implementations
 */
export class RethinkDocumentSnapshot<T> implements DocumentSnapshot<T> {
  constructor(
    public id: string,
    private _data: any | undefined,
    public exists: boolean,
    public ref: DocumentReference<T>,
    private converter: FirestoreDataConverter<T> | null = null,
  ) {}

  data(): T | undefined {
    if (!this._data) return undefined;
    if (this.converter) {
      const rawSnap = new RethinkQueryDocumentSnapshot<any>(this.id, this._data, this.ref);
      return this.converter.fromFirestore(rawSnap);
    }
    return this._data as T;
  }
}

export class RethinkQueryDocumentSnapshot<T>
  extends RethinkDocumentSnapshot<T>
  implements QueryDocumentSnapshot<T>
{
  constructor(id: string, data: T, ref: DocumentReference<T>) {
    super(id, data, true, ref);
  }
  data(): T {
    return super.data()!;
  }
}
